<template>
  <f7-page ref="page">
    <f7-navbar>
      <f7-nav-left>
        <f7-link icon-if-ios="f7:menu" icon-if-md="material:menu" panel-open="left"></f7-link>
      </f7-nav-left>
      <f7-nav-title>
        <navbar-center title="訂單管理" :subtitle="currentChannel.getFilterConfig().name" @click="toggleSearchbar" />
      </f7-nav-title>
      <f7-nav-right>
        <f7-link @click="syncAllOrderChannels">
          <font-awesome-icon icon="sync-alt" />
        </f7-link>
        <f7-link @click="setFullscreen(true)">
          <font-awesome-icon icon="expand" />
        </f7-link>
      </f7-nav-right>
    </f7-navbar>

    <f7-searchbar
      ref="orderListSearchbar"
      v-if="showSearchbar"
      disable-link-text="Cancel"
      :form="false"
      placeholder="歷史訂單搜尋"
      :clear-button="true"
      @click:clear="onSearchbarClear"
      @click:disable="onSearchbarDisable"
      @compositionstart.native="isComposingSearch = true"
      @compositionend.native="onSearchCompositionEnd"
      @input="onSearchbarInput($event.target.value)" />
    <template v-if="showResults || !showSearchbar">
      <f7-list v-if="currentChannel.orderList && currentChannel.orderList.length !== 0" style="margin: 0px 0px 60px 0px;">
        <order-list-item
          slot="list"
          v-for="(order, index) in currentChannel.orderList"
          :key="`kitchen-order-${index}`"
          :order="order"
          :fontSize="settings.fontSize"
          :margin="settings.margin"
          :filterConfig="currentChannel.getFilterConfig()" />
        <f7-button v-if="canLoadMore" slot="list" @click="loadMore">載入更多</f7-button>
      </f7-list>
      <div v-else style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 20px;"> 沒有訂單</div>
      <f7-link id="filter-shortcut" @click="isMainChannel = !isMainChannel">
        <div id="filter-config-content-amount">{{ shortcutChannel.orderList.length }}</div>
        <div id="filter-config-title">{{ shortcutChannel.getFilterConfig().name }}</div>
      </f7-link>
    </template>
    <template v-else>
      <f7-button raised @click="quickSearch(filterConfigs.getUnpaidFilterConfig)" class="filter-config-button">尚未付款 (內用、外帶)</f7-button>
      <f7-button raised @click="quickSearch(filterConfigs.getKitchenFilterConfig)" class="filter-config-button">廚房控單畫面</f7-button>
      <f7-button raised @click="quickSearch(filterConfigs.getDoneFilterConfig)" class="filter-config-button">尚未取餐</f7-button>
      <f7-button raised @click="quickSearch(filterConfigs.getPreOrderFilterConfig)" class="filter-config-button">預約外帶</f7-button>
      <f7-button raised @click="quickSearch(filterConfigs.getDeliveredFilterConfig)" class="filter-config-button">已完成</f7-button>
      <f7-button v-if="platformUtils.isApp(platform)" raised @click="scanQrCode()" class="filter-config-button">QR Code 掃描</f7-button>
    </template>
  </f7-page>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
// import OrderList from '../components/OrderList'
import OrderListItem from '../components/OrderListItem'
import NavbarCenter from '@/common/components/NavbarCenter'
import moment from 'moment'
import { storeClient } from '@/service/apiClients'
import caseTempState from '@Usecase/storeCase/tempState'
import managerWorkStation from '@Manager/storeManager/workStation'
import caseWorkStation from '@Usecase/storeCase/workStation'
import { playBumblebee, stopAudio } from '@/service/audioPlayer'

// 搜尋每頁筆數。與 loadMore 共用同一個值，offset 才會對齊（第一頁 0、下一頁 20…）。
const SEARCH_PAGE_SIZE = 20
// 打字到真正送出查詢之間的等待。store.order 在 store_id / received_at / nickname 上都
// 沒有索引，每次搜尋都是一次全表掃描；逐字送等於一個 keystroke 掃一次全表，中文 IME
// 組字期間（ㄐ→ㄐㄧ→ㄐㄧㄥ…）更誇張，這是 DB CPU 被打爆的直接原因。
const SEARCH_DEBOUNCE_MS = 400

export default {
  dependencies: [
    'platformUtils',
    'filterConfigs',
    'diningPreferenceType'
  ],
  mixins: [caseTempState, managerWorkStation, caseWorkStation],
  data () {
    return {
      platform: process.env.VUE_APP_PLATFORM,
      isMainChannel: true,
      showSearchbar: false,
      showResults: true,
      newOrderAlertPlaying: false,
      sound: null,
      syncOrderIntervalId: null,
      searchOffset: 0,
      searchDebounceId: null,
      isComposingSearch: false
    }
  },
  props: {
    storeIdProp: String
  },
  computed: {
    ...mapState(['currentMinute', 'socketId']),
    ...mapState('orderModule', ['newOrder', 'channels']),
    ...mapState('userModule', ['uid']),
    ...mapState(['settings']),
    ...mapGetters(['storeId']),
    ...mapGetters('itemstateModule', ['getStationItemState']),
    ...mapGetters('orderModule', ['mainOrderList', 'secondaryOrderList']),
    currentChannel () { return this.channels[this.isMainChannel ? 'main' : 'secondary'] },
    shortcutChannel () { return this.channels[this.isMainChannel ? 'secondary' : 'main'] },
    // 「已完成」與「搜尋」是唯二會分頁的清單；其餘 filterConfig 都是有時間範圍的
    // 營運清單，一次抓完，不需要載入更多。
    canLoadMore () {
      const id = this.currentChannel.getFilterConfig().id
      if (id === 'delivered') { return true }
      if (id !== 'search') { return false }
      // 搜尋結果常常只有一兩筆，上一頁沒拿滿就代表沒有下一頁——不要顯示按了沒反應的按鈕。
      return this.currentChannel.orderList.length >= this.searchOffset
    }
  },
  watch: {
    workStationMap (map) {
      this.syncAllOrderChannels()
    },
    socketId (id) {
      if (this.uid) { // 代表 Auth 過了
        this.syncAllOrderChannels()
        if (id) {
          clearInterval(this.syncOrderIntervalId)
        } else {
          this.syncOrderIntervalId = setInterval(this.syncAllOrderChannels, 5000)
        }
      }
    },
    async uid (v) {
      if (this.storeId) {
        Object.keys(this.channels).forEach(channel => { this.syncOrderChannel({ channel }) })
      }
    },
    newOrder (newOrder) {
      if (newOrder.diningPreference === this.diningPreferenceType.preOrder && this.settings.preOrderRing) {
        const that = this
        playBumblebee(true)

        this.newOrderAlertPlaying = true
        this.$f7.notification.create({
          title: '網路預約訂單',
          titleRightText: '下單時間: ' + moment(newOrder.receivedAt).format('MM/DD HH:mm'),
          subtitle: newOrder.nickname,
          text: '取餐時間: ' + moment(newOrder.pickupTime).format('MM/DD HH:mm'),
          closeOnClick: true,
          closeButton: true,
          on: {
            close () {
              stopAudio()
              that.newOrderAlertPlaying = false
            }
          }
        }).open()
      }
    }
  },
  async created () {
    if (!this.storeId) {
      await this.setStore(this.storeIdProp)
    }
    this.downloadMenu({})
    if (this.uid) {
      this.syncStationList()
      this.syncStoreTempState()
    }
    await this.asyncStoreData()
    this.syncAllOrderChannels()
  },
  beforeDestroy () {
    clearInterval(this.syncOrderIntervalId)
    clearTimeout(this.searchDebounceId)
  },
  methods: {
    ...mapActions(['setStore', 'setFullscreen', 'asyncStoreData']),
    ...mapActions('orderModule', ['syncOrderChannel']),
    ...mapActions('menuModule', ['downloadMenu']),
    syncAllOrderChannels () {
      Object.keys(this.channels).forEach(channel => { this.syncOrderChannel({ channel }) })
      this.searchOffset = 0
    },
    toggleSearchbar () {
      if (!this.showSearchbar) {
        this.showResults = false
      }
      this.showSearchbar = !this.showSearchbar
      this.cancelPendingSearch()
    },
    async loadMore () {
      try {
        this.$f7.dialog.progress()
        this.showResults = true
        this.showSearchbar = false
        const limit = SEARCH_PAGE_SIZE
        await this.syncOrderChannel({ channel: this.isMainChannel ? 'main' : 'secondary', params: { offset: this.searchOffset, limit } })
        this.searchOffset += limit
        this.$utils.nextFrame(() => this.$f7.dialog.close())
      } catch (e) {
        console.error(e.message)
        this.$utils.nextFrame(() => this.$f7.dialog.close())
        this.$f7.dialog.alert('無法取得餐點', '網路連線失敗')
      }
    },
    onSearchbarClear () {
      this.cancelPendingSearch()
      this.showResults = false
    },
    onSearchbarDisable (e) {
      this.cancelPendingSearch()
      this.showSearchbar = false
    },
    // 搜尋列被關掉或清空時，丟掉還在等待的查詢，並解除 IME 組字旗標——組字打到一半按
    // 取消會讓 compositionend 不再觸發，旗標卡住的話之後就再也送不出查詢。
    cancelPendingSearch () {
      clearTimeout(this.searchDebounceId)
      this.searchDebounceId = null
      this.isComposingSearch = false
    },
    getSearchFilterConfig (keywords) {
      return (params = {}) => ({
        id: 'search',
        name: '搜尋: ' + keywords,
        keywords,
        // 不帶 orderBy 的話後端會落到預設的 receivedAt ASC，等於從最舊的單開始給；
        // 查歷史訂單要的是最近的優先。
        orderBy: [['receivedAt', 'DESC']],
        limit: params.limit || SEARCH_PAGE_SIZE,
        offset: params.offset || 0
      })
    },
    onSearchbarInput (keywords) {
      // 組字期間的中間值（注音／拼音片段）一律不送，等 compositionend 帶完整字串進來。
      if (this.isComposingSearch) { return }
      this.queueSearch(keywords)
    },
    onSearchCompositionEnd (e) {
      this.isComposingSearch = false
      this.queueSearch(e.target.value)
    },
    queueSearch (keywords) {
      clearTimeout(this.searchDebounceId)
      const trimmed = (keywords || '').trim()
      if (!trimmed) {
        this.searchDebounceId = null
        this.showResults = false
        return
      }
      this.searchDebounceId = setTimeout(() => this.runSearch(trimmed), SEARCH_DEBOUNCE_MS)
    },
    runSearch (keywords) {
      this.searchDebounceId = null
      // 搜尋結果是寫進 main channel 的，畫面要跟著切過去才看得到（與 quickSearch 一致）。
      this.isMainChannel = true
      this.showResults = true
      this.searchOffset = SEARCH_PAGE_SIZE
      this.syncOrderChannel({
        channel: 'main',
        getFilterConfig: this.getSearchFilterConfig(keywords),
        params: { offset: 0, limit: SEARCH_PAGE_SIZE }
      }).catch(e => { console.error('[orders] 搜尋失敗: ' + e.message) })
    },
    async quickSearch (getFilterConfig) {
      this.isMainChannel = true
      try {
        this.$f7.dialog.progress()
        this.showResults = true
        this.showSearchbar = false
        this.searchOffset = 0
        const limit = 40
        await this.syncOrderChannel({ channel: 'main', getFilterConfig, params: { offset: this.searchOffset, limit } })
        this.searchOffset += limit
        this.$utils.nextFrame(() => this.$f7.dialog.close())
      } catch (e) {
        this.$utils.nextFrame(() => this.$f7.dialog.close())
        this.$f7.dialog.alert('無法取得餐點', '網路連線失敗')
      }
    },
    scanQrCode () {
      // eslint-disable-next-line
      cordova.plugins.barcodeScanner.scan(async result => {
        const { data: order } = await storeClient.get(`/stores/${this.storeId}/orders/${result.text}`)
        this.$f7router.navigate(`/stores/${this.storeId}/order-details/${order.id}?t=details`)
      }, error => {
        alert('Scanning failed: ' + error)
      }, {
        preferFrontCamera: false, // iOS and Android
        showFlipCameraButton: true, // iOS and Android
        showTorchButton: true, // iOS and Android
        torchOn: false, // Android, launch with the torch switched on (if available)
        saveHistory: false, // Android, save scan history (default false)
        prompt: '請將 QR Code 對準框框', // Android
        resultDisplayDuration: 500, // Android, display scanned text for X ms. 0 suppresses it entirely, default 1500
        formats: 'QR_CODE,PDF_417', // default: all but PDF_417 and RSS_EXPANDED
        orientation: 'portrait', // Android only (portrait|landscape), default unset so it rotates with the device
        disableAnimations: true, // iOS
        disableSuccessBeep: false // iOS and Android
      })
    }
  },
  components: {
    // OrderList,
    OrderListItem,
    NavbarCenter
  }
}

</script>

<style lang="scss" scoped>
#filter-shortcut {
  position: fixed;
  display: flex;
  flex-direction: column;
  right: 20px;
  bottom: 15px;
  background-color: rgba(0, 0, 0, 0.4);
  padding: 5px;
  border-radius: 5px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  overflow: hidden;
  min-width: 100px;
  z-index: 10;
}
#filter-config-content-amount {
  width: 60px;
  height: 60px;
  line-height: 60px;
  text-align: center;
  border-radius: 50%;
  font-size: 36px;
  color: white;
  // box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.4);
  // background-color: #4CAF50;
}
#filter-config-title {
  font-size: 14px;
  color: white;
}
.filter-config-button {
  margin: 5px 10px;
  height: 100px;
  line-height: 100px;
  font-size: 24px;
}
</style>
